import React, { useState, useMemo } from 'react';
import { Button, Select, Tooltip, Spin } from 'antd';
import { LeftOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';

const { Option } = Select;

const WEEKDAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
const MONTH_NAMES = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];
const STATUS_LABELS = { approved: 'تایید شده', pending: 'در انتظار', rejected: 'رد شده' };
const DEFAULT_STATUS_OPTIONS = [
  { value: 'pending', label: 'در انتظار' },
  { value: 'approved', label: 'تایید شده' },
  { value: 'rejected', label: 'رد شده' },
];

const LeavesCalendarView = ({
  leaves = [],
  loading = false,
  onMonthChange,
  onFilterChange,
  filterOptions = {},
}) => {
  const [jYear, setJYear] = useState(() => moment().jYear());
  const [jMonth, setJMonth] = useState(() => moment().jMonth());
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedLeaveType, setSelectedLeaveType] = useState('all');

  const employees = Array.isArray(filterOptions.employees) ? filterOptions.employees : [];
  const leaveTypes = Array.isArray(filterOptions.leave_types) ? filterOptions.leave_types : [];
  const statusOptions = useMemo(() => {
    const fromApi = Array.isArray(filterOptions.statuses) ? filterOptions.statuses : [];
    if (!fromApi.length) return DEFAULT_STATUS_OPTIONS;
    return fromApi
      .map((s) => ({
        value: String(s.value ?? s.id ?? ''),
        label: s.label || s.name || String(s.value ?? s.id ?? ''),
      }))
      .filter((s) => s.value);
  }, [filterOptions.statuses]);

  const triggerLoad = (y, m, overrides = {}) => {
    const daysInMonth = moment.jDaysInMonth(y, m);
    const params = {
      start_date: `${y}/${String(m + 1).padStart(2, '0')}/01`,
      end_date: `${y}/${String(m + 1).padStart(2, '0')}/${String(daysInMonth).padStart(2, '0')}`,
      leave_type: overrides.leave_type !== undefined ? overrides.leave_type : selectedLeaveType,
      status: overrides.status !== undefined ? overrides.status : selectedStatus,
      user_id: overrides.user_id !== undefined ? overrides.user_id : selectedEmployee,
    };
    if (onFilterChange) onFilterChange(params);
    else if (onMonthChange) onMonthChange(params);
  };

  const handlePrev = () => {
    let y = jYear, m = jMonth;
    if (m === 0) { m = 11; y -= 1; } else { m -= 1; }
    setJYear(y); setJMonth(m);
    triggerLoad(y, m);
  };

  const handleNext = () => {
    let y = jYear, m = jMonth;
    if (m === 11) { m = 0; y += 1; } else { m += 1; }
    setJYear(y); setJMonth(m);
    triggerLoad(y, m);
  };

  const handleToday = () => {
    const y = moment().jYear(), m = moment().jMonth();
    setJYear(y); setJMonth(m);
    triggerLoad(y, m);
  };

  const handleStatusFilterChange = (val) => {
    setSelectedStatus(val);
    triggerLoad(jYear, jMonth, { status: val });
  };
  const handleEmployeeFilterChange = (val) => {
    setSelectedEmployee(val);
    triggerLoad(jYear, jMonth, { user_id: val });
  };
  const handleLeaveTypeFilterChange = (val) => {
    setSelectedLeaveType(val);
    triggerLoad(jYear, jMonth, { leave_type: val });
  };

  const calendarGrid = useMemo(() => {
    const daysInMonth = moment.jDaysInMonth(jYear, jMonth);
    const firstOfMonth = moment(`${jYear}/${jMonth + 1}/1`, 'jYYYY/jM/jD');
    const gregDay = firstOfMonth.day();
    const startWeekday = gregDay === 6 ? 0 : gregDay + 1;
    const weeks = [];
    let dayOffset = -startWeekday;
    while (dayOffset + 1 <= daysInMonth) {
      const week = [];
      for (let col = 0; col < 7; col++) {
        const day = dayOffset + col + 1;
        if (day >= 1 && day <= daysInMonth) {
          const m = moment(`${jYear}/${jMonth + 1}/${day}`, 'jYYYY/jM/jD');
          week.push({
            day,
            date: m.format('YYYY-MM-DD'),
            isToday: moment().jYear() === jYear && moment().jMonth() === jMonth && moment().jDate() === day,
            isFriday: col === 6,
          });
        } else {
          week.push(null);
        }
      }
      weeks.push(week);
      dayOffset += 7;
    }
    return weeks;
  }, [jYear, jMonth]);

  const eventsByDate = useMemo(() => {
    const map = {};
    (leaves || []).forEach((leave) => {
      const startStr = leave.start ? leave.start.split('T')[0] : null;
      const endStr = leave.end ? leave.end.split('T')[0] : null;
      if (startStr) {
        const s = moment(startStr);
        const e = endStr ? moment(endStr) : s.clone();
        const cursor = s.clone();
        while (cursor.isSameOrBefore(e, 'day')) {
          const key = cursor.format('YYYY-MM-DD');
          if (!map[key]) map[key] = [];
          map[key].push(leave);
          cursor.add(1, 'day');
        }
      }
    });
    return map;
  }, [leaves]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 max-w-7xl mx-auto" style={{ fontFamily: 'CustomFont' }}>
      {/* فیلترها */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">نوع مرخصی</label>
          <Select
            value={selectedLeaveType}
            onChange={handleLeaveTypeFilterChange}
            style={{ width: '100%', fontFamily: 'CustomFont' }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">همه</Option>
            {leaveTypes.map((lt) => (
              <Option key={lt.id} value={String(lt.id)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {lt.color && <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: lt.color, display: 'inline-block' }} />}
                  {lt.type_name || lt.name}
                </span>
              </Option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">وضعیت</label>
          <Select value={selectedStatus} onChange={handleStatusFilterChange} style={{ width: '100%', fontFamily: 'CustomFont' }}>
            <Option value="all">همه</Option>
            {statusOptions.map((status) => (
              <Option key={status.value} value={status.value}>
                {status.label}
              </Option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">کارمند</label>
          <Select
            value={selectedEmployee}
            onChange={handleEmployeeFilterChange}
            style={{ width: '100%', fontFamily: 'CustomFont' }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">همه</Option>
            {employees.map((emp) => (
              <Option key={emp.id} value={String(emp.id)}>{emp.name}</Option>
            ))}
          </Select>
        </div>
      </div>

      {/* ناوبری */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Button onClick={handleToday} style={{ fontFamily: 'CustomFont' }}>امروز</Button>
          <Button icon={<RightOutlined />} onClick={handlePrev} />
          <Button icon={<LeftOutlined />} onClick={handleNext} />
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xl font-bold text-gray-800 dark:text-gray-200" style={{ fontFamily: 'CustomFont' }}>
            {MONTH_NAMES[jMonth]} {jYear}
          </div>
          {loading && <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
        </div>
        <div />
      </div>

      {/* تقویم */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" dir="rtl">
        <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-900/50">
          {WEEKDAYS.map((day, i) => (
            <div
              key={day}
              className={`text-center text-sm font-bold py-3 border-b border-gray-200 dark:border-gray-700 ${
                i === 6 ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
              }`}
              style={{ fontFamily: 'CustomFont' }}
            >
              {day}
            </div>
          ))}
        </div>
        {calendarGrid.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {week.map((cell, ci) => {
              if (!cell) {
                return <div key={ci} className="min-h-[90px] border-b border-l border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30" />;
              }
              const dayEvents = eventsByDate[cell.date] || [];
              return (
                <div
                  key={ci}
                  className={`min-h-[90px] border-b border-l border-gray-100 dark:border-gray-700 p-1.5 transition-colors ${
                    cell.isToday ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <div
                    className={`text-sm font-semibold mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                      cell.isToday ? 'bg-blue-600 text-white' : cell.isFriday ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                    }`}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {cell.day}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 2).map((ev, ei) => (
                      <Tooltip key={ei} title={`${ev.title || 'مرخصی'} - ${STATUS_LABELS[ev.status] || ev.status}`}>
                        <div
                          className="text-[10px] leading-tight px-1.5 py-0.5 rounded truncate text-white cursor-default"
                          style={{
                            backgroundColor: ev.color || '#1890ff',
                            fontFamily: 'CustomFont',
                            opacity: ev.status === 'rejected' ? 0.5 : 1,
                          }}
                        >
                          {ev.title || 'مرخصی'}
                        </div>
                      </Tooltip>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 px-1" style={{ fontFamily: 'CustomFont' }}>
                        +{dayEvents.length - 2} مورد دیگر
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeavesCalendarView;
